import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { ArrowLeft, Send, Mail, MessageCircle, CheckCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { Button, Card, Input, Select, Textarea } from '../components/ui';
import { linkButtonClass } from '../utils/linkButtonClass';
import { useSubmitContact } from '../hooks/mutations/useSubmitContact';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { mutateAsync, isPending } = useSubmitContact();

  const categories = [
    'サービスについて',
    '料金・プランについて',
    '技術的な問題',
    '機能リクエスト',
    'その他'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await mutateAsync(formData);
      setSubmitted(true);
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      setError(detail || 'お問い合わせの送信に失敗しました。時間をおいて再度お試しください。');
    }
  };

  if (submitted) {
    return (
      <Layout>
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary-hover">
              <ArrowLeft className="w-5 h-5 mr-2" />
              トップに戻る
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12">
          <Card padding="none" className="p-8 text-center">
            <div className="w-20 h-20 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h1 className="text-2xl font-bold text-text-heading mb-4">お問い合わせを受け付けました</h1>
            <p className="text-text-sub mb-8">
              ご連絡ありがとうございます。<br />
              内容を確認の上、通常2〜3営業日以内にご返信いたします。
            </p>
            <Link to="/" className={linkButtonClass('primary', 'inline-flex')}>
              トップに戻る
            </Link>
          </Card>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-primary hover:text-primary-hover">
            <ArrowLeft className="w-5 h-5 mr-2" />
            トップに戻る
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-text-heading mb-4">お問い合わせ</h1>
          <p className="text-primary">ご質問やご要望がございましたらお気軽にご連絡ください</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* 連絡先情報 */}
          <div className="md:col-span-1 space-y-6">
            <Card>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-text-heading">メール</h3>
              </div>
              <p className="text-text-sub text-sm">support@hoshikusaya.com</p>
            </Card>

            <Card>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-info-bg rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-info" />
                </div>
                <h3 className="font-semibold text-text-heading">Discord</h3>
              </div>
              <p className="text-text-sub text-sm">コミュニティに参加して質問</p>
              <a
                href="https://discord.gg/hGckF2bnfM"
                target="_blank"
                rel="noopener noreferrer"
                className="text-info hover:text-info-fg text-sm font-medium"
              >
                Discordに参加 →
              </a>
            </Card>

            <Card>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <h3 className="font-semibold text-text-heading">X (Twitter)</h3>
              </div>
              <a
                href="https://twitter.com/otomochi_com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-sub hover:text-text text-sm font-medium"
              >
                @otomochi_com →
              </a>
            </Card>
          </div>

          {/* お問い合わせフォーム */}
          <div className="md:col-span-2">
            <Card padding="none" className="p-8">
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-text mb-2">
                      お名前 <span className="text-danger">*</span>
                    </label>
                    <Input
                      id="contact-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="山田 太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-text mb-2">
                      メールアドレス <span className="text-danger">*</span>
                    </label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-category" className="block text-sm font-medium text-text mb-2">
                      お問い合わせ種別 <span className="text-danger">*</span>
                    </label>
                    <Select
                      id="contact-category"
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="">選択してください</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-text mb-2">
                      お問い合わせ内容 <span className="text-danger">*</span>
                    </label>
                    <Textarea
                      id="contact-message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="resize-none"
                      placeholder="お問い合わせ内容をご記入ください"
                    />
                  </div>

                  {error && (
                    <div
                      role="alert"
                      className="bg-danger-bg text-danger-fg px-4 py-3 rounded-field text-sm"
                    >
                      {error}
                    </div>
                  )}

                  <Button type="submit" loading={isPending} className="w-full">
                    {isPending ? (
                      <span>送信中...</span>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>送信する</span>
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </main>
    </Layout>
  );
}
